import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { departmentService } from "#/features/departments/api";
import { roundTo } from "#/lib/utils";
import { requisitionApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";
import type { RequisitionLine } from "../../types";

type LineRow = Omit<RequisitionLine, "id">;

export default function RequisitionCreatePage() {
  const navigate = useNavigate();

  const { data: depts, loading: deptsLoading } = useAsyncData(
    () => departmentService.list({ size: 100 }).then((r) => r.content),
    [],
  );

  const [departmentId, setDepartmentId] = useState<number | "">("");
  const [budgetLine, setBudgetLine] = useState("");
  const [purpose, setPurpose] = useState("");
  const [lines, setLines] = useState<LineRow[]>([
    { lineOrder: 1, description: "", quantity: 1, itemType: "CONSUMABLE", inventoryItemId: null, assetCategoryId: null },
  ]);

  const { mutate, loading, error } = useMutation(async () => {
    if (!departmentId) throw new Error("Please select a department.");
    if (!purpose.trim()) throw new Error("Purpose is required.");
    const result = await requisitionApi.create({
      departmentId: departmentId as number,
      budgetLine: budgetLine || null,
      purpose,
      lines: lines.map((l) => ({
        lineOrder: l.lineOrder,
        description: l.description,
        quantity: roundTo(l.quantity, 4),
        itemType: l.itemType ?? "CONSUMABLE",
        inventoryItemId: l.inventoryItemId ?? undefined,
        assetCategoryId: l.assetCategoryId ?? undefined,
      })),
    });
    navigate(`/procurement/requisitions/${result.id}`);
  });

  function addLine() {
    setLines((prev) => [...prev, { lineOrder: prev.length + 1, description: "", quantity: 1, itemType: "CONSUMABLE", inventoryItemId: null, assetCategoryId: null }]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineOrder: i + 1 })));
  }

  function updateLine(idx: number, field: keyof LineRow, value: string | number) {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/requisitions" className="text-sm text-emerald-700 hover:underline">&larr; Back to Requisitions</Link>
      </div>

      <ProcForm formNo="PROC-FRM-01" title="Purchase Requisition">
        {deptsLoading && <ProcLoading label="Loading departments..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="Requisition Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Department *</label>
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : "")}
                className="w-full rounded-xl border px-3 py-2 text-sm"
                required
              >
                <option value="">Select department...</option>
                {(depts ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Budget Line</label>
              <input
                type="text"
                value={budgetLine}
                onChange={(e) => setBudgetLine(e.target.value)}
                placeholder="e.g. Capital Expenditure 2026"
                className="w-full rounded-xl border px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Purpose *</label>
              <textarea
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                rows={3}
                placeholder="Describe the purpose of this requisition..."
                className="w-full rounded-xl border px-3 py-2 text-sm"
                required
              />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left w-32">Item Type</th>
                  <th className="px-3 py-2 text-right w-24">Qty</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((line, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-slate-400">{line.lineOrder}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => updateLine(idx, "description", e.target.value)}
                        placeholder="Item description"
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={line.itemType ?? "CONSUMABLE"}
                        onChange={(e) => updateLine(idx, "itemType", e.target.value)}
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                      >
                        <option value="CONSUMABLE">Consumable</option>
                        <option value="CAPITAL_ASSET">Capital Asset</option>
                        <option value="SERVICE">Service</option>
                        <option value="WORKS">Works</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={line.quantity}
                        min={1}
                        onChange={(e) => updateLine(idx, "quantity", Number(e.target.value))}
                        className="w-full rounded-lg border px-2 py-1.5 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                        className="text-red-500 hover:text-red-700 disabled:opacity-30 text-xs font-bold"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={addLine}
            className="mt-3 rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
          >
            + Add Line
          </button>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/requisitions" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => mutate()}
            disabled={loading}
            className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {loading ? "Saving…" : "Save Requisition"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
